// src/components/LearnMore.js
import React, { useState, useRef, useEffect } from 'react';
import HamburgerNav from './HamburgerNav';
import routePreloader from '../services/routePreloader';
import './LearnMore.css';

const LearnMore = () => {
  const [activePopup, setActivePopup] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');
  
  const carouselRef = useRef(null);

  useEffect(() => {
    // Preload store routes data and images
    routePreloader.preloadRoutes()
      .then(() => console.log('Store routes preloaded from LearnMore'))
      .catch(error => console.error('Error preloading routes:', error));

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    // Observe elements
    const elements = document.querySelectorAll('.service-card, .services-tagline-section, .contact-section');
    elements.forEach(el => {
      observer.observe(el);
    });

    // Cleanup
    return () => {
      elements.forEach(el => {
        observer.unobserve(el);
      });
    };
  }, []);

  // Service cards data
  const serviceCards = [
    {
      id: 1,
      smiley: ':)',
      title: 'Pick Your Adventure',
      description: 'Choose when and where. Whether it is a three day weekend adventure or a thirty day odyssey, BackPass has you covered.'
    },
    {
      id: 2,
      smiley: ':))',
      title: 'Book with Our App',
      description: 'Our companion app lets you see availibility, connect with others, and reserve your spot at any participating hostel or go off route at the best possible price.'
    },
    {
      id: 3,
      smiley: ':)))',
      title: 'Travel Your Way',
      description: 'Change your mind? Want to stay an extra night somewhere amazing? Your pass moves with you, giving you the flexibility every backpacker craves.'
    },
    {
      id: 4,
      smiley: ':))))',
      title: 'Live Your Life',
      description: 'Real Hostels, Real Experiences. No corporate chains. No tourist traps. Our favorite partners are family-run mountain lodges, the beachside staples.'
    }
  ];

  // Handle popup
  const handlePopupClick = (type) => {
    if (activePopup === type) {
      setActivePopup(null);
    } else {
      setActivePopup(type);
    }
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  // Handle carousel touch events
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentSlide < serviceCards.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
    if (isRightSwipe && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      // Use your existing REACT_APP_BACKEND_URL
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${baseURL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: 'BackPass Contact Form Submission'
        }),
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus(''), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 5000);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="learn-more-container">
      {/* Hero Section */}
      <section 
        className="hero-section"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.4)), url('/images/hatgirl.webp') center/cover`
        }}
      >
        <HamburgerNav animationDelay="0.4s" />
        
        <div className="hero-content">
          <a href="/" className="hero-logo">
            <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
          </a>
          <p className="hero-subtitle">Backpacking at its Best</p>
          
          {/* Expandable Buttons */}
          <div className="expandable-buttons">
            <button 
              className={`expand-button ${activePopup === 'backpacking' ? 'active' : ''}`}
              onClick={() => handlePopupClick('backpacking')}
            >
              Backpacking Basics
            </button>
            <button 
              className={`expand-button ${activePopup === 'why-pass' ? 'active' : ''}`}
              onClick={() => handlePopupClick('why-pass')}
            >
              Why the Pass
            </button>
            <button 
              className={`expand-button ${activePopup === 'albanian-alpine' ? 'active' : ''}`}
              onClick={() => handlePopupClick('albanian-alpine')}
            >
              Why Start in Albania
            </button>
            <button 
              className={`expand-button ${activePopup === 'join-beta' ? 'active' : ''}`}
              onClick={() => handlePopupClick('join-beta')}
            >
              Why an Inaugural Pass?
            </button>
          </div>
        </div>
      </section>

      {/* Popup Overlay */}
      {activePopup && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            {activePopup === 'backpacking' ? (
              <>
                <h2>What is Backpacking</h2>
                <p>Backpacking is for people who seek experience. Minimal materials and social gatherings mean you will find people and places overflowing with enthusiasm. The freedom and flexibility is unlike anything you will have known so far. Some solo-travel continents, others form friendships that last a lifetime. Many fall in love. When you backpack you can be whoever you want to be, but pretty soon you'll realize there is no one you'd rather be besides who you really are. Meeting that person is what it means to backpack with BackPass.</p>
              </>
            ) : activePopup === 'why-pass' ? (
              <>
                <h2>Why the Pass</h2>
                <p>It has been shown that the majority of travelers spend much more than they plan to. With BackPass you'll avoid becoming a statistic. One pass, one price, while staying at our hand-picked partner hostels along carefully crafted routes. These are places we've personally visited, loved, and believe in.</p>
                <p>I'm writing this as the founder of BackPass. I created this because I wish it existed when I first went backpacking. The flexibility combined the with confidence that you're staying somewhere solid will save so many people so much stress during the best moments of their life.</p>
              </>
            ) : activePopup === 'albanian-alpine' ? (
              <>
                <h2>Why the Albanian Alpine</h2>
                <p>We're launching with Albania – you'll soon see why.</p>
                <p>Albania is remarkably affordable. Cheap flights from anywhere in Europe, nestled between Croatia and Greece, you can find a country with pristine beaches, mountains that touch the sky, and a culture of hospitality and cuisine that will leave you wanting more.</p>
              </>
            ) : (
              <>
                <h2>On the Come Up</h2>
                <p>The BackPass Inaugural Pass means you will have personalized communication from BackPass every step of the way. Think of it as the cheapest travel agent you will ever have. In return we'll use your experience to help shape the future of travel.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Combined Services and Tagline Section with Background */}
      <section 
        className="services-tagline-section"
        style={{
          background: `linear-gradient(rgba(7, 34, 49, 0.85), rgba(7, 34, 49, 0.85)), url('/images/mainbackground.jpg') center/cover`
        }}
      >
        <h2 className="section-title">Step-By-Step</h2>
        
        {/* Desktop Grid */}
        <div className="services-container desktop-view">
          {serviceCards.map((card, index) => (
            <div key={card.id} className="service-card" style={{ animationDelay: `${index * 0.1}s` }}>
              <span className="service-number">{card.smiley}</span>
              <h3 className="service-title">{card.title}</h3>
              <p className="service-description">{card.description}</p>
            </div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="services-container mobile-view">
          <div 
            className="carousel-wrapper"
            ref={carouselRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div 
              className="carousel-track"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {serviceCards.map((card) => (
                <div key={card.id} className="carousel-slide">
                  <div className="service-card">
                    <span className="service-number">{card.smiley}</span>
                    <h3 className="service-title">{card.title}</h3>
                    <p className="service-description">{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Dot Indicators */}
          <div className="carousel-dots">
            {serviceCards.map((_, index) => (
              <button
                key={index}
                className={`dot ${currentSlide === index ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Tagline Content */}
        <div className="tagline-content">
          <p className="sub-tagline">Get your pass. Pack your bag. Be Alive. BackPass.</p>
          <a href="/go" className="go-button">Go</a>
        </div>
      </section>

      {/* CTA Section with Contact Form */}
      <section 
        className="cta-section"
        style={{
          background: `url('/images/BackPassBackgroundGuy.webp') center/cover`
        }}
      >
        {/* Contact Section */}
        <div className="contact-section">
          <h2>Hit Us Up</h2>
          
          <form className="contact-form" onSubmit={handleFormSubmit}>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <textarea
              name="message"
              placeholder="Your Message"
              rows="5"
              value={formData.message}
              onChange={handleInputChange}
              required
            />
            <button type="submit" disabled={formStatus === 'sending'}>
              {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
            </button>
            
            {formStatus === 'success' && (
              <p className="form-status success">Message sent successfully!</p>
            )}
            {formStatus === 'error' && (
              <p className="form-status error">Failed to send message. Please try again.</p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default LearnMore;